import type { AnalyticsActionMeta } from "domains/analytics/types";
import type { CollectionIdOnly, CollectionMeta } from "domains/collection/types";
import { DestinationTypes } from "domains/types";
import type {
  FetchFollowedTopicsListDefaultAction,
  FetchFollowedTopicsListFulfilledAction,
  FetchFollowedTopicsListRejectedAction,
  FetchHighlightsDefaultAction,
  FetchHighlightsFulfilledAction,
  FetchHighlightsRejectedAction,
  FetchSharedWithMeAction,
  FetchSharedWithMeFulfilledAction,
  FetchTopicDefaultAction,
  FetchTopicFulfilledAction,
  FetchTopicRejectedAction,
  FetchTopicsForCollectionAction,
  FetchTopicsForCollectionFailureAction,
  FetchTopicsForCollectionSuccessAction,
  FetchTopicsFulfilledAction,
  FetchTopicsRejectedAction,
  FollowTopicDefaultAction,
  FollowTopicFulfilledAction,
  FollowTopicRejectedAction,
  ToggleTopicMachineTranslationAction,
  UnfollowTopicDefaultAction,
  UnfollowTopicFulfilledAction,
  UnfollowTopicRejectedAction,
} from "entities/topics/actionTypes";
import { TopicActionType } from "entities/topics/actionTypes";
import type {
  FetchCustomTopicsPayload,
  FetchFollowedTopicsPayload,
  FetchTopicsForCollectionPayload,
  FetchTopicsPayload,
  FollowTopicPayload,
  TopicFulfilledPayload,
  TopicListPayload,
  TopicPayload,
} from "entities/topics/types";

export const fetchTopic = (payload: TopicPayload): FetchTopicDefaultAction => ({
  type: TopicActionType.FETCH_TOPIC,
  payload,
});

export const fetchTopicSuccess = (payload: TopicFulfilledPayload): FetchTopicFulfilledAction => ({
  type: TopicActionType.FETCH_TOPIC_FULFILLED,
  payload,
});

export const fetchTopicFailure = (id: string, meta: FetchTopicRejectedAction["meta"]): FetchTopicRejectedAction => ({
  type: TopicActionType.FETCH_TOPIC_REJECTED,
  payload: { id },
  meta,
});

export const fetchHighlightedTopics = (): FetchHighlightsDefaultAction => ({
  type: TopicActionType.FETCH_HIGHLIGHTS,
});

export const fetchHighlightedTopicsSuccess = (payload: FetchTopicsPayload): FetchHighlightsFulfilledAction => ({
  type: TopicActionType.FETCH_HIGHLIGHTS_FULFILLED,
  payload,
});

export const fetchHighlightedTopicsFailure = (
  meta: FetchHighlightsRejectedAction["meta"],
): FetchHighlightsRejectedAction => ({
  meta,
  type: TopicActionType.FETCH_HIGHLIGHTS_REJECTED,
});

export enum CustomMapOwner {
  SharedWithMe = "false",
  MyMaps = "true",
  SharedWithMeAndMyMaps = "",
}

export const fetchTopicsSuccess = (payload: TopicListPayload): FetchTopicsFulfilledAction => ({
  type: TopicActionType.FETCH_TOPICS_FULFILLED,
  payload,
});

export const fetchTopicsFailure = (
  type: string,
  meta: FetchTopicsRejectedAction["meta"],
): FetchTopicsRejectedAction => ({
  type: TopicActionType.FETCH_TOPICS_REJECTED,
  payload: { type },
  meta,
});

export const followTopic = (payload: FollowTopicPayload, meta?: AnalyticsActionMeta): FollowTopicDefaultAction => ({
  type: TopicActionType.FOLLOW,
  payload,
  meta,
});

export const followTopicSuccess = (
  payload: FollowTopicPayload,
  meta?: AnalyticsActionMeta,
): FollowTopicFulfilledAction => ({
  type: TopicActionType.FOLLOW_FULFILLED,
  payload,
  meta,
});

export const followTopicFailure = (
  payload: FollowTopicPayload,
  meta?: FollowTopicRejectedAction["meta"],
): FollowTopicRejectedAction => ({
  type: TopicActionType.FOLLOW_REJECTED,
  payload,
  meta,
});

export const unfollowTopic = (payload: FollowTopicPayload, meta?: AnalyticsActionMeta): UnfollowTopicDefaultAction => ({
  type: TopicActionType.UNFOLLOW,
  payload,
  meta,
});

export const unfollowTopicSuccess = (
  payload: FollowTopicPayload,
  meta?: AnalyticsActionMeta,
): UnfollowTopicFulfilledAction => ({
  type: TopicActionType.UNFOLLOW_FULFILLED,
  payload,
  meta,
});

export const unfollowTopicFailure = (
  payload: FollowTopicPayload,
  meta: UnfollowTopicRejectedAction["meta"],
): UnfollowTopicRejectedAction => ({
  type: TopicActionType.UNFOLLOW_REJECTED,
  payload,
  meta,
});

export type TopicsMeta = {
  canEdit?: boolean;
  limit: number;
  offset: number;
  owner?: CustomMapOwner;
  type?: DestinationTypes;
};

export const fetchFollowedTopics = (meta: TopicsMeta): FetchFollowedTopicsListDefaultAction => ({
  type: TopicActionType.FETCH_FOLLOWED_LIST,
  meta,
});

export const fetchFollowedTopicsSuccess = (
  payload: FetchFollowedTopicsPayload,
): FetchFollowedTopicsListFulfilledAction => ({
  type: TopicActionType.FETCH_FOLLOWED_LIST_FULFILLED,
  payload,
});

export const fetchFollowedTopicsFailure = (
  meta: FetchFollowedTopicsListRejectedAction["meta"],
): FetchFollowedTopicsListRejectedAction => ({
  meta,
  type: TopicActionType.FETCH_FOLLOWED_LIST_REJECTED,
});

export const fetchSharedWithMe = (meta: TopicsMeta): FetchSharedWithMeAction => ({
  type: TopicActionType.FETCH_SHARED_WITH_ME,
  payload: {
    limit: meta.limit,
    offset: meta.offset,
    type: DestinationTypes.CUSTOM,
    canEdit: true,
    owner: CustomMapOwner.SharedWithMe,
  },
});

export const fetchSharedWithMeSuccess = (payload: FetchCustomTopicsPayload): FetchSharedWithMeFulfilledAction => ({
  type: TopicActionType.FETCH_SHARED_WITH_ME_FULFILLED,
  payload,
});

export const toggleTopicMachineTranslation = (
  meta: ToggleTopicMachineTranslationAction["meta"],
): ToggleTopicMachineTranslationAction => ({
  type: TopicActionType.TOGGLE_TOPIC_MACHINE_TRANSLATION,
  meta,
});

export const fetchTopicsForCollection = (payload: FetchTopicsForCollectionPayload): FetchTopicsForCollectionAction => ({
  type: TopicActionType.FETCH_TOPICS_FOR_COLLECTION,
  payload,
});

export const fetchTopicsForCollectionSuccess = (
  payload: CollectionIdOnly,
  meta: CollectionMeta,
): FetchTopicsForCollectionSuccessAction => ({
  type: TopicActionType.FETCH_TOPICS_FOR_COLLECTION_SUCCESS,
  payload,
  meta,
});

export const fetchTopicsForCollectionFailure = (payload: CollectionIdOnly): FetchTopicsForCollectionFailureAction => ({
  type: TopicActionType.FETCH_TOPICS_FOR_COLLECTION_FAILURE,
  payload,
});
